import React, { createContext, useContext, useState, useCallback } from 'react';
import { AlertTriangle, X } from 'lucide-react';

const ConfirmContext = createContext(null);

export const ConfirmProvider = ({ children }) => {
    const [config, setConfig] = useState(null);

    const confirm = useCallback((message, options = {}) => {
        return new Promise((resolve) => {
            setConfig({
                message,
                title: options.title || '¿Estás seguro?',
                confirmText: options.confirmText || 'Sí, eliminar',
                cancelText: options.cancelText || 'Cancelar',
                type: options.type || 'danger', // danger, warning, info
                onConfirm: () => {
                    resolve(true);
                    setConfig(null);
                },
                onCancel: () => {
                    resolve(false);
                    setConfig(null);
                }
            });
        });
    }, []);

    return (
        <ConfirmContext.Provider value={{ confirm }}>
            {children}
            {config && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={config.onCancel} />
                    
                    <div 
                        className="relative w-full max-w-sm rounded-[28px] border shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200"
                        style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}
                    >
                        {/* Header decorativo según el tipo */}
                        <div className={`p-6 flex flex-col items-center text-center gap-4 ${
                            config.type === 'danger' ? 'bg-red-500/10' : 
                            config.type === 'warning' ? 'bg-amber-500/10' : 'bg-cyan-500/10'
                        }`}>
                            <div className={`p-4 rounded-2xl ${
                                config.type === 'danger' ? 'bg-red-500/20 text-red-500' : 
                                config.type === 'warning' ? 'bg-amber-500/20 text-amber-500' : 'bg-cyan-500/20 text-cyan-500'
                            }`}>
                                <AlertTriangle className="w-8 h-8" />
                            </div>
                            
                            <div>
                                <h3 className="text-lg font-black uppercase tracking-widest leading-tight" style={{ color: 'var(--text-primary)' }}>
                                    {config.title}
                                </h3>
                                <p className="text-sm mt-2 opacity-80 leading-relaxed font-medium" style={{ color: 'var(--text-secondary)' }}>
                                    {config.message}
                                </p>
                            </div>
                        </div>

                        {/* Botones de acción */}
                        <div className="p-4 flex flex-col gap-2">
                            <button
                                onClick={config.onConfirm}
                                className={`w-full py-4 rounded-2xl font-black uppercase tracking-widest text-xs shadow-lg transition-all active:scale-95 ${
                                    config.type === 'danger' ? 'bg-red-500 text-white shadow-red-500/20' : 
                                    config.type === 'warning' ? 'bg-amber-500 text-white shadow-amber-500/20' : 'bg-cyan-500 text-white shadow-cyan-500/20'
                                }`}
                            >
                                {config.confirmText}
                            </button>
                            <button
                                onClick={config.onCancel}
                                className="w-full py-4 rounded-2xl font-black uppercase tracking-widest text-xs transition-all hover:bg-white/5"
                                style={{ color: 'var(--text-muted)' }}
                            >
                                {config.cancelText}
                            </button>
                        </div>

                        {/* Botón de cerrar superior */}
                        <button 
                            onClick={config.onCancel}
                            className="absolute top-4 right-4 p-2 opacity-40 hover:opacity-100 transition-opacity"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </ConfirmContext.Provider>
    );
};

export const useConfirm = () => {
    const context = useContext(ConfirmContext);
    if (!context) throw new Error('useConfirm must be used within a ConfirmProvider');
    return context;
};
